import { useState, useEffect } from 'react';
import {
  Plus, Send, Eye, Copy, Trash2, ExternalLink, Edit3,
  FileText, Clock, CheckCircle2, XCircle, AlertCircle, MoreVertical
} from 'lucide-react';
import { proposalService, type ProposalListItem } from '../../services/proposalService';
import { supabase } from '../../services/supabaseService';
import type { Proposal } from '../../types/proposal-blocks';
import { SendContractModal } from '../contracts/SendContractModal';

interface ProposalsListProps {
  onEdit: (id: string) => void;
  onCreate: () => void;
}

const STATUS_CONFIG: Record<Proposal['status'], { label: string; color: string; icon: any }> = {
  draft: { label: 'Borrador', color: 'bg-gray-100 text-gray-700', icon: FileText },
  sent: { label: 'Enviada', color: 'bg-blue-100 text-blue-700', icon: Send },
  viewed: { label: 'Vista', color: 'bg-purple-100 text-purple-700', icon: Eye },
  accepted: { label: 'Aceptada', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle },
  expired: { label: 'Expirada', color: 'bg-orange-100 text-orange-700', icon: AlertCircle },
};

interface ProposalMetrics {
  total: number;
  accepted: number;
  pending: number;
  acceptanceRate: number;
  avgDaysToAccept: number | null;
}

export function ProposalsList({ onEdit, onCreate }: ProposalsListProps) {
  const [proposals, setProposals] = useState<ProposalListItem[]>([]);
  const [metrics, setMetrics] = useState<ProposalMetrics>({ total: 0, accepted: 0, pending: 0, acceptanceRate: 0, avgDaysToAccept: null });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Proposal['status'] | 'all'>('all');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [contractModalProposal, setContractModalProposal] = useState<Proposal | null>(null);

  useEffect(() => { loadProposals(); }, [filter]);
  useEffect(() => { loadMetrics(); }, []);

  // Realtime subscription for proposal events (viewed, accepted)
  useEffect(() => {
    const channel = supabase
      .channel('proposal-events-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'proposal_events' },
        (payload) => {
          const event = payload.new as any;
          if (event.event_type === 'viewed' || event.event_type === 'accepted') {
            // Refresh list to show updated status
            loadProposals();
            // Show toast
            const msg = event.event_type === 'accepted'
              ? `🎉 ¡Propuesta aceptada por ${event.actor_name || 'cliente'}!`
              : `👁 Propuesta vista por un cliente`;
            showToast(msg, 'success');
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const loadProposals = async () => {
    try {
      setLoading(true);
      const data = await proposalService.getProposals(filter === 'all' ? undefined : filter);
      setProposals(data);
    } catch (e) {
      showToast('Error cargando propuestas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('id, status, sent_at, accepted_at');
      if (error || !data) return;

      const total = data.length;
      const accepted = data.filter((p) => p.status === 'accepted').length;
      const pending = data.filter((p) => ['sent', 'viewed'].includes(p.status)).length;
      const acceptanceRate = total > 0 ? Math.round((accepted / total) * 100) : 0;

      // Avg days to accept
      const acceptedWithDates = data.filter((p) => p.status === 'accepted' && p.sent_at && p.accepted_at);
      let avgDaysToAccept: number | null = null;
      if (acceptedWithDates.length > 0) {
        const totalDays = acceptedWithDates.reduce((sum, p) => {
          const days = (new Date(p.accepted_at!).getTime() - new Date(p.sent_at!).getTime()) / (1000 * 60 * 60 * 24);
          return sum + days;
        }, 0);
        avgDaysToAccept = Math.round(totalDays / acceptedWithDates.length);
      }

      setMetrics({ total, accepted, pending, acceptanceRate, avgDaysToAccept });
    } catch {}
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSend = async (proposal: ProposalListItem) => {
    if (!proposal.client_email) {
      showToast('No hay email de cliente asignado', 'error');
      return;
    }
    if (!confirm(`¿Enviar propuesta "${proposal.title}" a ${proposal.client_email}?`)) return;

    setSendingId(proposal.id);
    try {
      await proposalService.sendProposal(proposal.id);
      showToast('Propuesta enviada correctamente', 'success');
      loadProposals();
    } catch (e: any) {
      showToast(e.message || 'Error al enviar', 'error');
    } finally {
      setSendingId(null);
    }
  };

  const handleCopyLink = (slug: string) => {
    const url = proposalService.getPublicUrl(slug);
    navigator.clipboard.writeText(url);
    showToast('Enlace copiado al portapapeles', 'success');
  };

  const handleDuplicate = async (id: string) => {
    try {
      await proposalService.duplicateProposal(id);
      showToast('Propuesta duplicada', 'success');
      loadProposals();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
  };

  const handleDelete = async (proposal: ProposalListItem) => {
    if (!confirm(`¿Eliminar "${proposal.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await proposalService.deleteProposal(proposal.id);
      showToast('Propuesta eliminada', 'success');
      loadProposals();
    } catch (e: any) {
      showToast(e.message, 'error');
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filters: { value: Proposal['status'] | 'all'; label: string }[] = [
    { value: 'all', label: 'Todas' },
    { value: 'draft', label: 'Borradores' },
    { value: 'sent', label: 'Enviadas' },
    { value: 'viewed', label: 'Vistas' },
    { value: 'accepted', label: 'Aceptadas' },
    { value: 'expired', label: 'Expiradas' },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Propuestas</h1>
          <p className="text-sm text-gray-500 mt-1">{proposals.length} propuesta{proposals.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={onCreate}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          Nueva propuesta
        </button>
      </div>

      {/* Metrics */}
      {metrics.total > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{metrics.total}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-green-600">{metrics.acceptanceRate}%</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Tasa aceptación</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-blue-600">{metrics.pending}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Pendientes</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-2xl font-bold text-purple-600">{metrics.avgDaysToAccept !== null ? `${metrics.avgDaysToAccept}d` : '—'}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Media aceptación</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              filter === f.value
                ? 'bg-blue-100 text-blue-700'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : proposals.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay propuestas{filter !== 'all' ? ` con estado "${STATUS_CONFIG[filter as Proposal['status']]?.label}"` : ''}</p>
            <button
              onClick={onCreate}
              className="mt-3 text-blue-600 text-sm font-medium hover:underline"
            >
              Crear primera propuesta
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Propuesta</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {proposals.map((p) => {
                const statusConf = STATUS_CONFIG[p.status];
                const StatusIcon = statusConf.icon;
                return (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => onEdit(p.id)}
                        className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors text-left"
                      >
                        {p.title}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{p.client_name || '—'}</span>
                      {p.client_email && (
                        <span className="block text-xs text-gray-400">{p.client_email}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusConf.color}`}>
                        <StatusIcon size={12} />
                        {statusConf.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(p.sent_at || p.created_at)}
                      {p.first_viewed_at && (
                        <span className="block text-xs text-gray-400">
                          Vista: {formatDate(p.first_viewed_at)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="relative inline-block">
                        <div className="flex items-center gap-1">
                          {/* Quick actions */}
                          {p.status === 'draft' && (
                            <button
                              onClick={() => handleSend(p)}
                              disabled={sendingId === p.id}
                              className="p-1.5 rounded hover:bg-blue-50 text-blue-600 transition-colors disabled:opacity-50"
                              title="Enviar al cliente"
                            >
                              <Send size={14} />
                            </button>
                          )}
                          {p.status === 'accepted' && (
                            <button
                              onClick={async () => {
                                const full = await proposalService.getProposal(p.id);
                                setContractModalProposal(full);
                              }}
                              className="p-1.5 rounded hover:bg-green-50 text-green-600 transition-colors"
                              title="Enviar contrato"
                            >
                              <FileText size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => handleCopyLink(p.public_slug)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 transition-colors"
                            title="Copiar enlace público"
                          >
                            <Copy size={14} />
                          </button>
                          <a
                            href={proposalService.getPublicUrl(p.public_slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 transition-colors"
                            title="Ver propuesta"
                          >
                            <ExternalLink size={14} />
                          </a>

                          {/* More menu */}
                          <button
                            onClick={() => setActionMenuId(actionMenuId === p.id ? null : p.id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-400 transition-colors"
                          >
                            <MoreVertical size={14} />
                          </button>
                        </div>

                        {/* Dropdown */}
                        {actionMenuId === p.id && (
                          <div
                            className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[160px]"
                            onMouseLeave={() => setActionMenuId(null)}
                          >
                            <button
                              onClick={() => { onEdit(p.id); setActionMenuId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Edit3 size={14} /> Editar
                            </button>
                            <button
                              onClick={() => { handleDuplicate(p.id); setActionMenuId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Copy size={14} /> Duplicar
                            </button>
                            <div className="border-t border-gray-100 my-1" />
                            <button
                              onClick={() => { handleDelete(p); setActionMenuId(null); }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} /> Eliminar
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm font-medium z-50 ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Contract modal */}
      {contractModalProposal && (
        <SendContractModal
          isOpen={true}
          onClose={() => setContractModalProposal(null)}
          proposal={contractModalProposal}
        />
      )}
    </div>
  );
}
